import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { JsonToMjml } from '@go-mailer/easy-email-core';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { useCampaignService } from 'hooks/mailing/campaign';
import { useAudienceService } from 'hooks/users/audience';
import { AttachmentDetails, CampaignDetails } from './details';
import { Spacer } from 'app/layouts/generic';
import styles from './preview.module.css';
import useScreenSize from 'hooks/size';
import { useEventsService } from 'hooks/automation/event';
import mjml2html from 'mjml-browser';

const resolveBody = async (body) => {
  if (typeof body === 'string') return body;

  const { html } = await mjml2html(
    JsonToMjml({
      data: body.content,
      mode: 'production',
      context: body.content
    })
  );
  return html;
};

export const CampaignPreview = () => {
  const { id } = useParams();
  const { fetchAudiences } = useAudienceService();
  const { fetchCampaign } = useCampaignService();
  const { fetchEvents } = useEventsService();
  const { isMobile, isDesktop } = useScreenSize();

  const [events_by_code, setEventsByCode] = useState([]);
  const [campaign, setCampaign] = useState({});
  const [campaign_body, setCampaignBody] = useState('');
  const [campaign_audience, setCampaignAudience] = useState([]);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    fetchCampaign(id).then(async ({ campaign }) => {
      setCampaign(campaign);
      setCampaignBody(
        campaign.is_advanced ? await resolveBody(JSON.parse(campaign.template)) : campaign.body
      );
      getCampaignAudience(campaign.mailing_lists);
      setAttachments(campaign.attachments);
    });

    fetchEvents({ query_string: 'type=user' }).then(({ events }) => {
      setEventsByCode(events.reduce((sac, ev) => ({ ...sac, [ev.code.split(':')[0]]: ev }), {}));
    });
  }, []);

  const getCampaignAudience = (audience_ids = []) => {
    fetchAudiences({
      query_string: `id=${audience_ids.toString()}&return_only=name`
    }).then(({ audiences, error }) => {
      if (error) {
        return;
      }
      setCampaignAudience(audiences);
    });
  };

  return (
    <>
      {isMobile && (
        <>
          <GridRow>
            <GridColumn span={4}>
              <div className={cx(styles.iframeWrapper, 'shadow-sm campaign-view my-md-1')}>
                <iframe
                  className="w-100"
                  title="Campaign body"
                  style={{
                    width: '100%',
                    minHeight: '75vh',
                    padding: '15px',
                    background: 'var(--neutral-light)',
                    border: '1px solid var(--neutral-dark-5)',
                    borderRadius: '8px'
                  }}
                  srcDoc={campaign_body}
                ></iframe>
              </div>
            </GridColumn>
          </GridRow>
          <GridRow>
            <GridColumn span={4}>
              <CampaignDetails campaign={campaign} audiences={campaign_audience} />
              <Spacer multiple={4} />
              {attachments?.length > 0 && <AttachmentDetails attachments={attachments} />}
            </GridColumn>
          </GridRow>
        </>
      )}

      {!isMobile && (
        <GridRow num_of_columns={5}>
          <GridColumn span={isDesktop ? 4 : 3}>
            <div className={cx(styles.iframeWrapper, 'shadow-sm campaign-view my-md-1')}>
              <iframe
                className="w-100"
                title="Campaign body"
                style={{
                  width: '100%',
                  minHeight: '75vh',
                  padding: '10px',
                  background: 'var(--neutral-light)',
                  border: '1px solid var(--neutral-dark-5)',
                  borderRadius: '8px'
                }}
                srcDoc={campaign_body}
              ></iframe>
            </div>
          </GridColumn>
          <GridColumn span={isDesktop ? 1 : 2}>
            <CampaignDetails
              campaign={campaign}
              audiences={campaign_audience}
              events={events_by_code}
            />
            <Spacer multiple={4} />
            {attachments?.length > 0 && <AttachmentDetails attachments={attachments} />}
          </GridColumn>
        </GridRow>
      )}
    </>
  );
};
